import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { debounce, cloneDeep } from "lodash";
import { AppDispatch } from "../configureStore";
import { FormFieldValue } from "../interface/formTypes";
import { updateReturnEntry, batchUpdateReturnEntries } from "../redux/reducer/returns";
import { formTypeDefinitions } from "../utils/generatedFormTypes";
import { FormExtractions } from "../interface/returns";

interface UseFormFieldStateOptions {
    interfaceName: keyof typeof formTypeDefinitions;
    currentPageIndex: number;
    debounceMs?: number;
    formType: keyof FormExtractions;
}

interface FieldChange {
    fieldPath: string;
    value: FormFieldValue;
    fullPath: string;
}

export const useFormFieldState = (initialData: any, options: UseFormFieldStateOptions) => {
    const dispatch = useDispatch<AppDispatch>();
    const { interfaceName, currentPageIndex, debounceMs = 150, formType } = options;

    // Local state for immediate UI updates
    const [localData, setLocalData] = useState<any>(initialData);
    // Track pending changes for batch updates
    const pendingChangesRef = useRef<Map<string, FieldChange>>(new Map());

    // Use ref to track current data without causing re-renders
    const localDataRef = useRef<any>(initialData);

    // Use ref for interfaceName to avoid recreating debounced function
    const interfaceNameRef = useRef(interfaceName);
    interfaceNameRef.current = interfaceName;

    // Debounced function to dispatch Redux updates
    const debouncedDispatch = useMemo(
        () =>
            debounce((changes: Map<string, FieldChange>) => {
                if (changes.size === 0) return;

                // If we have multiple changes, use batch update
                if (changes.size > 1) {
                    const updates = Array.from(changes.values()).map((change) => ({
                        interfaceName: interfaceNameRef.current,
                        fieldPath: change.fieldPath,
                        fullExtractedJsonPath: change.fullPath,
                        value: change.value,
                    }));

                    dispatch(batchUpdateReturnEntries(updates));
                } else {
                    // Single change, use regular update
                    const change = Array.from(changes.values())[0];
                    dispatch(
                        updateReturnEntry(interfaceNameRef.current, change.fieldPath, change.fullPath, change.value),
                    );
                }

                // Clear pending changes after dispatch
                pendingChangesRef.current.clear();
            }, debounceMs),
        [dispatch, debounceMs],
    );

    // Track page index to detect navigation vs Redux feedback
    const prevPageIndexRef = useRef<number | null>(null);

    // Only reset local state on initial mount or page navigation,
    // NOT on Redux feedback from local edits (which would overwrite optimistic updates)
    useEffect(() => {
        if (prevPageIndexRef.current === null || prevPageIndexRef.current !== currentPageIndex) {
            // Flush pending changes to Redux before resetting
            if (prevPageIndexRef.current !== null) {
                debouncedDispatch.flush();
            }
            setLocalData(initialData);
            localDataRef.current = initialData;
            pendingChangesRef.current.clear();
            prevPageIndexRef.current = currentPageIndex;
        }
    }, [initialData, currentPageIndex, debouncedDispatch]);

    // Handle field changes with immediate local update
    const handleFieldChange = useCallback(
        (fieldPath: string, value: FormFieldValue) => {
            // Update local state immediately for responsive UI
            setLocalData((prevData: any) => {
                // Deep clone to avoid mutation; initialize if missing so we can build the path
                const newData = prevData != null ? cloneDeep(prevData) : {};
                const keys = fieldPath.split(".");
                let current = newData;

                // Navigate to the nested field, creating intermediate objects as needed
                for (let i = 0; i < keys.length - 1; i++) {
                    const key = keys[i];
                    if (current[key] == null || typeof current[key] !== "object") {
                        current[key] = {};
                    }
                    current = current[key];
                }

                // Set the value
                current[keys[keys.length - 1]] = value;

                // Update ref as well
                localDataRef.current = newData;

                return newData;
            });

            // Determine the full path for Redux update
            // Adjust this based on your actual Redux structure
            const fullPath = `${formType}.extraction[${currentPageIndex}].extractedData.data.${fieldPath}`;

            // Track the change
            pendingChangesRef.current.set(fieldPath, {
                fieldPath,
                value,
                fullPath,
            });

            // Trigger debounced dispatch
            debouncedDispatch(new Map(pendingChangesRef.current));
        },
        [currentPageIndex, debouncedDispatch, formType],
    );

    // Get value from local state
    const getFieldValue = useCallback((path: string) => {
        if (!localDataRef.current) return "";
        const keys = path.split(".");
        const value = keys.reduce((acc, key) => acc?.[key], localDataRef.current);
        return value ?? "";
    }, []);

    // Force save all pending changes immediately
    const saveChanges = useCallback(() => {
        if (pendingChangesRef.current.size > 0) {
            debouncedDispatch.cancel();
            debouncedDispatch(new Map(pendingChangesRef.current));
        }
    }, [debouncedDispatch]);

    // Cancel pending changes and revert to initial data
    const cancelChanges = useCallback(() => {
        debouncedDispatch.cancel();
        setLocalData(initialData);
        pendingChangesRef.current.clear();
    }, [debouncedDispatch, initialData]);

    // Cleanup on unmount
    useEffect(
        () => () => {
            debouncedDispatch.cancel();
        },
        [debouncedDispatch],
    );

    return {
        localData,
        handleFieldChange,
        getFieldValue,
        saveChanges,
        cancelChanges,
    };
};
